import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import { usePage } from '@inertiajs/react';
import FlashMessages from '@/Shared/FlashMessages';
import TopHeader from '@/Shared/TopHeader';
import BottomHeader from '@/Shared/BottomHeader';
import axios from 'axios';
import { getCsrfToken, refreshToken, setCsrfToken } from '../utils';
import NotificationAlertBox from '@/Components/NotificationAlertBox';

export default function Layout({ title, children }) {
	const { user, cart, csrf_token } = usePage().props;
	const [siteCart, setSiteCart] = useState(cart);
	// move these to use route() when the time is right but for now it throws href not defined errors
	const modifyCart = async ({ items = [], setIsLoading = () => {} }) => {
		try {
			const response = await axios.post('prescriptions/update-cart', {
				_token: getCsrfToken(),
				rx_items: items.map(rx => {
					return {
						action: rx.action || 'add',
						rx_number: rx.rx_number || '',
						rx_name: rx.rx_name || '',
						rx_category: rx.category || '',
						cold_pack: rx.cold_pack || false,
						zero_copay_script: rx.zero_copay_script || false
					};
				})
			});

			if (!response || !response?.data) return;
			if (response.data?.csrf_token)
				document
					.querySelector('meta[name="csrf-token"]')
					.setAttribute('content', response.data.csrf_token);
			if (!response.data?.status === 'success')
				throw new Error('Cart update failed to process');
			if (response.data?.cart) setSiteCart(response.data.cart);
		} catch (error) {
			if (error.response.status === 419) {
				try {
					await refreshToken();
					modifyCart({ items, setIsLoading });
				} catch (error) {
					alert(
						'We were unable to update your cart due to the following error: ' +
							error.message +
							'. We will refresh the page in an attempt to resolve this issue. If the problem persists, please contact support.'
					);
					window.location.reload();
					console.error(error);
				}
			}
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		setCsrfToken(csrf_token);
	}, [csrf_token]);

	return (
		<div>
			<Helmet titleTemplate={title} title={title} />
			<div className="flex flex-col">
				<div className="flex flex-col h-screen">
					<div className="md:flex flex-row w-full items-center border-b-2 border-accent bg-primary text-white box-shadow-header">
						<div className="flex mx-auto w-full">
							<TopHeader />
							<BottomHeader user={user} cart={siteCart} />
						</div>
					</div>
					{user && (
						<div className="flex flex-grow">
							{/* To reset scroll region (https://inertiajs.com/pages#scroll-regions) add `scroll-region="true"` to div below */}
							<div className="container mx-auto overflow-y-scroll no-scrollbar py-4">
								<FlashMessages />
								<NotificationAlertBox />
								{React.Children.map(children, child => {
									if (React.isValidElement(child)) {
										return React.cloneElement(child, {
											siteCart,
											modifyCart,
											setSiteCart
										});
									}
								})}
							</div>
						</div>
					)}
					{!user && (
						<div className="flex flex-grow overflow-hidden">
							<div className="w-full px-2 sm:px-4 py-4 sm:py-8 overflow-hidden overflow-y-auto md:p-12">
								<FlashMessages />
								{children}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
